import {
    Alert,
    Button,
    Col,
    DatePicker,
    Descriptions,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { apiCall } from '../../API/RestApi';
import { modalError } from '../../Component/Modal/ModalLayout';
import { useSelector } from 'react-redux';
import Search from 'antd/es/input/Search';
import dayjs from 'dayjs';

function AssetMaintenance() {
    const token = useSelector((state) => state.user.token);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [vendorOptions, setVendorOptions] = useState([]);
    const [searchAssetId, setSearchAssetId] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterType, setFilterType] = useState('');

    const [assetOptions, setAssetOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);

    const [isModal, setIsModal] = useState(false);
    const [dataModal, setDataModal] = useState(null);

    const [isDetailModal, setIsDetailModal] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [isAlert, setIsAlert] = useState(false);
    const [textAlert, setTextAlert] = useState('');

    const [page, setPage] = useState({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    const [form] = Form.useForm();
    const watchedDetails = Form.useWatch('details', form) || [];

    const titlePage = 'Maintenance Asset Data';

    const maintenanceTypeOptions = [
        { value: 'Preventive', label: 'Preventive' },
        { value: 'Corrective', label: 'Corrective' },
        { value: 'Repair', label: 'Repair' },
        { value: 'Inspection', label: 'Inspection' },
    ];

    const statusOptions = [
        { value: 'Pending', label: 'Pending' },
        { value: 'Scheduled', label: 'Scheduled' },
        { value: 'On Progress', label: 'On Progress' },
        { value: 'Completed', label: 'Completed' },
        { value: 'Canceled', label: 'Canceled' },
    ];

    const getHeaders = () => ({
        Authorization: 'Bearer ' + token,
    });

    const currencyFormat = (value) => Number(value || 0).toLocaleString('id-ID');

    const renderStatus = (status) => {
        if (status === 'Pending') return <Tag color="orange">{status}</Tag>;
        if (status === 'Scheduled') return <Tag color="blue">{status}</Tag>;
        if (status === 'On Progress') return <Tag color="processing">{status}</Tag>;
        if (status === 'Completed') return <Tag color="green">{status}</Tag>;
        if (status === 'Canceled') return <Tag color="red">{status}</Tag>;
        return <Tag>{status || '-'}</Tag>;
    };

    const detailCostTotal = Array.isArray(watchedDetails)
        ? watchedDetails.reduce((acc, item) => {
            const qty = Number(item?.qty || 0);
            const unitPrice = Number(item?.unit_price || 0);
            const totalPrice = Number(item?.total_price || 0);
            return acc + (totalPrice || qty * unitPrice);
        }, 0)
        : 0;

    const columns = [
        {
            title: 'No',
            key: 'no',
            width: 70,
            render: (_, __, index) =>
                (page.pagination.current - 1) * page.pagination.pageSize + index + 1,
        },
        {
            title: 'Asset',
            key: 'asset',
            width: 200,
            render: (_, record) => record?.asset?.asset_name || '-',
        },
        {
            title: 'Asset Code',
            key: 'asset_code',
            width: 140,
            render: (_, record) => record?.asset?.asset_code || '-',
        },
        {
            title: 'Category',
            key: 'category',
            width: 160,
            render: (_, record) => record?.asset?.category?.asset_category_name || '-',
        },
        {
            title: 'Type',
            dataIndex: 'maintenance_type',
            key: 'maintenance_type',
            width: 140,
            render: (text) => text || '-',
        },
        {
            title: 'Request Date',
            dataIndex: 'request_date',
            key: 'request_date',
            width: 130,
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
        },
        {
            title: 'Scheduled Date',
            dataIndex: 'scheduled_date',
            key: 'scheduled_date',
            width: 130,
            render: (text) => (text ? dayjs(text).format('YYYY-MM-DD') : '-'),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 130,
            render: (text) => renderStatus(text),
        },
        {
            title: 'Vendor',
            dataIndex: 'vendor_id',
            key: 'vendor_id',
            width: 180,
            render: (value) => {
                const vendor = vendorOptions.find((item) => item.value === value);
                return vendor?.label || value || '-';
            },
        },
        {
            title: 'Cost Total',
            dataIndex: 'cost_total',
            key: 'cost_total',
            width: 140,
            render: (text) => currencyFormat(text),
        },
        {
            title: 'Detail Items',
            key: 'details_count',
            width: 100,
            render: (_, record) => record?.details?.length || 0,
        },
        {
            title: 'Action',
            key: 'action',
            width: 190,
            fixed: 'right',
            render: (_, record) => (
                <Space>
                    <Tooltip title="Detail">
                        <Button onClick={() => openDetailModal(record)}>
                            <EyeOutlined />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Edit">
                        <Button type="primary" onClick={() => openModal('edit', record)}>
                            <EditOutlined />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Delete">
                        <Button danger onClick={() => modalDeleteData(record)}>
                            <DeleteOutlined />
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        loadMaintenances();
    }, [JSON.stringify(page.pagination), searchAssetId, filterStatus, filterType]);

    useEffect(() => {
        loadAssets();
        loadUsers();
        loadVendors();
    }, []);

    const loadVendors = () => {
        const dataReq = {
            method: 'GET',
            url: '/vendors?nopage=1',
            data: {
                headers: getHeaders(),
            },
        };

        apiCall(dataReq)
            .then((res) => {
                if (res.status === 'success') {
                    const list = Array.isArray(res?.data?.data)
                        ? res.data.data
                        : Array.isArray(res?.data)
                            ? res.data
                            : [];

                    const options = list.map((item) => ({
                        value: item.vendor_id,
                        label:
                            item.vendor_name ||
                            item.vendor_code ||
                            `Vendor #${item.vendor_id}`,
                    }));

                    setVendorOptions(options);
                }
            })
            .catch((e) => {
                console.log(e.message);
            });
    };

    const loadMaintenances = () => {
        const params = new URLSearchParams({
            page: page.pagination.current,
            limit: page.pagination.pageSize,
        });

        if (searchAssetId) params.append('asset_id', searchAssetId);
        if (filterStatus) params.append('status', filterStatus);
        if (filterType) params.append('maintenance_type', filterType);

        getApi('GET', `/asset-maintenances?${params.toString()}`);
    };

    const getApi = (method, url) => {
        setLoading(true);

        const dataReq = {
            method,
            url,
            data: {
                headers: getHeaders(),
            },
        };

        apiCall(dataReq)
            .then((res) => {
                if (res.status === 'success') {
                    setData(res.data.data || []);
                    setPage((prev) => ({
                        ...prev,
                        pagination: {
                            ...prev.pagination,
                            total: res.data.total_data || 0,
                        },
                    }));
                } else {
                    modalError(res.message || 'Gagal mengambil data');
                }
                setLoading(false);
            })
            .catch((e) => {
                setLoading(false);
                modalError(e.message);
            });
    };

    const loadAssets = () => {
        const dataReq = {
            method: 'GET',
            url: '/assets?nopage=1',
            data: {
                headers: getHeaders(),
            },
        };

        apiCall(dataReq)
            .then((res) => {
                if (res.status === 'success') {
                    const list = Array.isArray(res?.data?.data) ? res.data.data : [];
                    const options = list.map((item) => ({
                        value: item.asset_id,
                        label: `${item.asset_name || '-'}${item.asset_code ? ` - ${item.asset_code}` : ''}`,
                    }));
                    setAssetOptions(options);
                }
            })
            .catch((e) => {
                console.log(e.message);
            });
    };

    const loadUsers = () => {
        const dataReq = {
            method: 'GET',
            url: '/selectusers',
            data: {
                headers: getHeaders(),
            },
        };

        apiCall(dataReq)
            .then((res) => {
                if (res.status === 'success') {
                    const list = Array.isArray(res?.data?.data)
                        ? res.data.data
                        : Array.isArray(res?.data)
                            ? res.data
                            : [];

                    const options = list.map((item) => ({
                        value: item.users_id,
                        label:
                            item.name ||
                            item.users_name ||
                            item.full_name ||
                            `${item.users_code || ''} ${item.name || ''}`.trim() ||
                            `User #${item.users_id}`,
                    }));
                    setUserOptions(options);
                }
            })
            .catch((e) => {
                console.log(e.message);
            });
    };

    const getDetailApi = (id) => {
        setLoadingDetail(true);

        const dataReq = {
            method: 'GET',
            url: `/asset-maintenances/${id}`,
            data: {
                headers: getHeaders(),
            },
        };

        apiCall(dataReq)
            .then((res) => {
                if (res.status === 'success') {
                    setDetailData(res.data);
                    setIsDetailModal(true);
                } else {
                    modalError(res.message || 'Gagal mengambil detail');
                }
                setLoadingDetail(false);
            })
            .catch((e) => {
                setLoadingDetail(false);
                modalError(e.message);
            });
    };

    const postApi = async (method, url, values) => {
        setLoading(true);
        setIsAlert(false);

        const dataReq = {
            method,
            url,
            data: {
                data: values,
                headers: getHeaders(),
            },
        };

        await apiCall(dataReq)
            .then((res) => {
                if (res.status === 'success') {
                    setDataModal(null);
                    setIsModal(false);
                    setIsAlert(true);
                    setTextAlert(res.message);
                    form.resetFields();
                    loadMaintenances();
                } else {
                    modalError(res.message || 'Gagal menyimpan data');
                }
                setLoading(false);
            })
            .catch((e) => {
                setLoading(false);
                modalError(e.message);
            });
    };

    const modalDeleteData = (record) => {
        Modal.confirm({
            title: 'NOTICE',
            content: `Do you want to delete maintenance for asset "${record?.asset?.asset_name || '-'}"?`,
            onOk: () => postApi('DELETE', `/asset-maintenances/${record.asset_maintenance_id}`, null),
        });
    };

    const openModal = (type, data) => {
        if (type === 'edit' && data) {
            setDataModal(data);

            form.setFieldsValue({
                asset_id: data.asset_id,
                maintenance_type: data.maintenance_type,
                request_date: data.request_date ? dayjs(data.request_date) : null,
                scheduled_date: data.scheduled_date ? dayjs(data.scheduled_date) : null,
                start_date: data.start_date ? dayjs(data.start_date) : null,
                end_date: data.end_date ? dayjs(data.end_date) : null,
                issue_description: data.issue_description,
                action_taken: data.action_taken,
                vendor_id: data.vendor_id,
                status: data.status,
                performed_by: data.performed_by,
                approved_by: data.approved_by,
                next_maintenance_date: data.next_maintenance_date ? dayjs(data.next_maintenance_date) : null,
                notes: data.notes,
                details:
                    data.details?.length > 0
                        ? data.details.map((d) => ({
                            item_type: d.item_type,
                            item_name: d.item_name,
                            qty: d.qty,
                            unit_price: d.unit_price,
                            total_price: d.total_price,
                            notes: d.notes,
                        }))
                        : [
                            {
                                item_type: '',
                                item_name: '',
                                qty: 1,
                                unit_price: 0,
                                total_price: 0,
                                notes: '',
                            },
                        ],
            });
        } else {
            setDataModal(null);
            form.resetFields();
            form.setFieldsValue({
                status: 'Pending',
                request_date: dayjs(),
                details: [
                    {
                        item_type: '',
                        item_name: '',
                        qty: 1,
                        unit_price: 0,
                        total_price: 0,
                        notes: '',
                    },
                ],
            });
        }

        setIsModal(true);
    };

    const openDetailModal = (record) => {
        getDetailApi(record.asset_maintenance_id);
    };

    const closeModal = () => {
        setDataModal(null);
        setIsModal(false);
        form.resetFields();
    };

    const closeDetailModal = () => {
        setIsDetailModal(false);
        setDetailData(null);
    };

    const handleTableChange = (pagination) => {
        setPage({ pagination });
    };

    const searchData = (value) => {
        setSearchAssetId(value);
        setPage((prev) => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                current: 1,
            },
        }));
    };

    const resetFilter = () => {
        setSearchAssetId('');
        setFilterStatus('');
        setFilterType('');
        setPage({
            pagination: {
                current: 1,
                pageSize: 10,
                total: 0,
            },
        });
    };

    const onFinish = (values) => {
        const payload = {
            ...values,
            request_date: values.request_date ? dayjs(values.request_date).format('YYYY-MM-DD') : '',
            scheduled_date: values.scheduled_date ? dayjs(values.scheduled_date).format('YYYY-MM-DD') : '',
            start_date: values.start_date ? dayjs(values.start_date).format('YYYY-MM-DD') : '',
            end_date: values.end_date ? dayjs(values.end_date).format('YYYY-MM-DD') : '',
            next_maintenance_date: values.next_maintenance_date
                ? dayjs(values.next_maintenance_date).format('YYYY-MM-DD')
                : '',
            details: (values.details || []).map((item) => {
                const qty = Number(item?.qty || 0);
                const unitPrice = Number(item?.unit_price || 0);
                const totalPrice = Number(item?.total_price || 0);

                return {
                    item_type: item?.item_type || '',
                    item_name: item?.item_name || '',
                    qty,
                    unit_price: unitPrice,
                    total_price: totalPrice || qty * unitPrice,
                    notes: item?.notes || '',
                };
            }),
        };

        if (dataModal) {
            postApi('PUT', `/asset-maintenances/${dataModal.asset_maintenance_id}`, payload);
        } else {
            postApi('POST', '/asset-maintenances', payload);
        }
    };

    return (
        <div>
            <Helmet>
                <title>{titlePage}</title>
            </Helmet>

            {isAlert && (
                <Alert
                    message={textAlert}
                    type="success"
                    banner
                    closable
                    afterClose={() => setIsAlert(false)}
                />
            )}

            <h3>Maintenance Asset</h3>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                    <Search
                        placeholder="Filter by Asset ID"
                        allowClear
                        enterButton="Search"
                        size="middle"
                        onSearch={searchData}
                    />
                </Col>

                <Col span={5}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filter Status"
                        allowClear
                        value={filterStatus || undefined}
                        options={statusOptions}
                        onChange={(value) => {
                            setFilterStatus(value || '');
                            setPage((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: 1,
                                },
                            }));
                        }}
                    />
                </Col>

                <Col span={5}>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Filter Type"
                        allowClear
                        value={filterType || undefined}
                        options={maintenanceTypeOptions}
                        onChange={(value) => {
                            setFilterType(value || '');
                            setPage((prev) => ({
                                ...prev,
                                pagination: {
                                    ...prev.pagination,
                                    current: 1,
                                },
                            }));
                        }}
                    />
                </Col>

                <Col span={6} style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button onClick={resetFilter}>Reset</Button>
                    <Button type="primary" onClick={() => openModal('add', null)}>
                        <PlusOutlined />
                        <b style={{ marginLeft: 5 }}>Add</b>
                    </Button>
                </Col>
            </Row>

            <Table
                size="middle"
                columns={columns}
                dataSource={data}
                pagination={page.pagination}
                loading={loading}
                onChange={handleTableChange}
                rowKey={(record) => record.asset_maintenance_id}
                scroll={{ x: 2200 }}
            />

            <Modal
                title={dataModal ? 'Edit Maintenance Asset' : 'Add Maintenance Asset'}
                open={isModal}
                onOk={form.submit}
                onCancel={closeModal}
                okText="Save"
                width={1100}
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="asset_id"
                                label="Asset"
                                rules={[{ required: true, message: 'Asset is required!' }]}
                            >
                                <Select
                                    showSearch
                                    placeholder="Select asset"
                                    options={assetOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                name="maintenance_type"
                                label="Maintenance Type"
                                rules={[{ required: true, message: 'Maintenance Type is required!' }]}
                            >
                                <Select
                                    options={maintenanceTypeOptions}
                                    placeholder="Select maintenance type"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="request_date" label="Request Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" disabled />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="scheduled_date" label="Scheduled Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="next_maintenance_date" label="Next Maintenance Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="start_date" label="Start Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="end_date" label="End Date">
                                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="status" label="Status">
                                <Select options={statusOptions} placeholder="Select status" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="vendor_id" label="Vendor">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select vendor"
                                    options={vendorOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="performed_by" label="Performed By">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select user"
                                    options={userOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="approved_by" label="Approved By">
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder="Select user"
                                    options={userOptions}
                                    optionFilterProp="label"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="issue_description" label="Issue Description">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="action_taken" label="Action Taken">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="notes" label="Notes">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <h4>Maintenance Details</h4>

                    <Form.List name="details">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    <div
                                        key={key}
                                        style={{
                                            marginBottom: 16,
                                            padding: 16,
                                            border: '1px solid #f0f0f0',
                                            borderRadius: 8,
                                        }}
                                    >
                                        <Row gutter={16}>
                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'item_type']}
                                                    label="Item Type"
                                                >
                                                    <Input placeholder="Item type" />
                                                </Form.Item>
                                            </Col>

                                            <Col span={6}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'item_name']}
                                                    label="Item Name"
                                                    rules={[{ required: true, message: 'Item Name is required!' }]}
                                                >
                                                    <Input placeholder="Item name" />
                                                </Form.Item>
                                            </Col>

                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'qty']}
                                                    label="Qty"
                                                >
                                                    <InputNumber min={0} style={{ width: '100%' }} />
                                                </Form.Item>
                                            </Col>

                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'unit_price']}
                                                    label="Unit Price"
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        style={{ width: '100%' }}
                                                        formatter={(value) =>
                                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                        }
                                                        parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={4}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'total_price']}
                                                    label="Total Price"
                                                >
                                                    <InputNumber
                                                        min={0}
                                                        style={{ width: '100%' }}
                                                        formatter={(value) =>
                                                            `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                                        }
                                                        parser={(value) => value?.replace(/\$\s?|(,*)/g, '')}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Row gutter={16}>
                                            <Col span={20}>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'notes']}
                                                    label="Notes"
                                                >
                                                    <Input.TextArea rows={2} />
                                                </Form.Item>
                                            </Col>

                                            <Col
                                                span={4}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'end',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <Popconfirm
                                                    title="Delete this detail?"
                                                    onConfirm={() => remove(name)}
                                                    disabled={fields.length === 1}
                                                >
                                                    <Button danger disabled={fields.length === 1}>
                                                        Delete
                                                    </Button>
                                                </Popconfirm>
                                            </Col>
                                        </Row>

                                        <div style={{ fontSize: 12, color: '#666' }}>
                                            Detail #{index + 1}
                                        </div>
                                    </div>
                                ))}

                                <Button
                                    type="dashed"
                                    onClick={() =>
                                        add({
                                            item_type: '',
                                            item_name: '',
                                            qty: 1,
                                            unit_price: 0,
                                            total_price: 0,
                                            notes: '',
                                        })
                                    }
                                    block
                                    icon={<PlusOutlined />}
                                >
                                    Add Detail
                                </Button>
                            </>
                        )}
                    </Form.List>

                    <div style={{ marginTop: 16, fontWeight: 600 }}>
                        Estimasi Total: {currencyFormat(detailCostTotal)}
                    </div>
                </Form>
            </Modal>

            <Modal
                title="Detail Maintenance Asset"
                open={isDetailModal}
                onCancel={closeDetailModal}
                footer={[
                    <Button key="close" onClick={closeDetailModal}>
                        Close
                    </Button>,
                ]}
                width={1100}
            >
                <Descriptions bordered column={2} size="middle" loading={loadingDetail}>
                    <Descriptions.Item label="Maintenance ID">
                        {detailData?.asset_maintenance_id || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Asset">
                        {detailData?.asset?.asset_name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Asset Code">
                        {detailData?.asset?.asset_code || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Category">
                        {detailData?.asset?.category?.asset_category_name || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Maintenance Type">
                        {detailData?.maintenance_type || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                        {renderStatus(detailData?.status)}
                    </Descriptions.Item>

                    <Descriptions.Item label="Request Date">
                        {detailData?.request_date ? dayjs(detailData.request_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Scheduled Date">
                        {detailData?.scheduled_date ? dayjs(detailData.scheduled_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Start Date">
                        {detailData?.start_date ? dayjs(detailData.start_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="End Date">
                        {detailData?.end_date ? dayjs(detailData.end_date).format('YYYY-MM-DD') : '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Next Maintenance Date">
                        {detailData?.next_maintenance_date
                            ? dayjs(detailData.next_maintenance_date).format('YYYY-MM-DD')
                            : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Vendor">
                        {vendorOptions.find((item) => item.value === detailData?.vendor_id)?.label ||
                            detailData?.vendor_id ||
                            '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Performed By">
                        {detailData?.performed_by || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Approved By">
                        {detailData?.approved_by || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Cost Total">
                        {currencyFormat(detailData?.cost_total)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Detail Count">
                        {detailData?.details?.length || 0}
                    </Descriptions.Item>

                    <Descriptions.Item label="Issue Description" span={2}>
                        {detailData?.issue_description || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Action Taken" span={2}>
                        {detailData?.action_taken || '-'}
                    </Descriptions.Item>

                    <Descriptions.Item label="Notes" span={2}>
                        {detailData?.notes || '-'}
                    </Descriptions.Item>
                </Descriptions>

                <h4 style={{ marginTop: 20 }}>Maintenance Detail Items</h4>

                <Table
                    size="small"
                    rowKey={(record, index) => index}
                    dataSource={detailData?.details || []}
                    pagination={false}
                    columns={[
                        {
                            title: 'No',
                            key: 'no',
                            width: 60,
                            render: (_, __, index) => index + 1,
                        },
                        {
                            title: 'Item Type',
                            dataIndex: 'item_type',
                            key: 'item_type',
                            render: (text) => text || '-',
                        },
                        {
                            title: 'Item Name',
                            dataIndex: 'item_name',
                            key: 'item_name',
                            render: (text) => text || '-',
                        },
                        {
                            title: 'Qty',
                            dataIndex: 'qty',
                            key: 'qty',
                            render: (text) => text || 0,
                        },
                        {
                            title: 'Unit Price',
                            dataIndex: 'unit_price',
                            key: 'unit_price',
                            render: (text) => currencyFormat(text),
                        },
                        {
                            title: 'Total Price',
                            dataIndex: 'total_price',
                            key: 'total_price',
                            render: (text) => currencyFormat(text),
                        },
                        {
                            title: 'Notes',
                            dataIndex: 'notes',
                            key: 'notes',
                            render: (text) => text || '-',
                        },
                    ]}
                    scroll={{ x: 900 }}
                    style={{ marginTop: 12 }}
                />
            </Modal>
        </div>
    );
}

export default AssetMaintenance;